/**
 * The widget's UI. Behavior ported from `dashboard/widget/minirag-widget.js`
 * (bubble -> window, streamed reply) with two additions the old widget didn't
 * have: thumbs up/down feedback (the backend scope this widget authenticates
 * with now grants exactly that action) and basic keyboard/aria support. The
 * old widget's collapsible "sources used" panel was dropped: a visitor sees
 * the answer only, and the backend no longer sends widget sessions the
 * retrieved passages at all.
 */
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';

import {
  fetchWidgetSession,
  streamChat,
  submitFeedback,
  WidgetSessionError,
  type WidgetSession,
} from './api';
// Inlined into widget.js as a data URL by the library build (~1 kB), so the
// footer icon costs no extra request on the customer's page.
import brandIconUrl from './assets/ask360-icon.png';
import { MarkdownView } from './MarkdownView';

export interface WidgetProps {
  apiUrl: string;
  siteKey: string;
  title: string;
  /** Up to 4 starter questions shown as tappable chips on first open —
   * clicking one sends it as the visitor's first message. Empty → no chips. */
  suggestedQuestions?: string[] | undefined;
  position: 'bottom-right' | 'bottom-left';
  /** Launcher icon set from the dashboard's Customize dialog. Falls back to
   * the default chat-bubble glyph when unset. When set, the launcher shows
   * the image edge-to-edge with no colour fill or padding behind it. */
  iconUrl?: string | undefined;
  /** Message-input placeholder (null/empty → the built-in default). */
  inputPlaceholder?: string | undefined;
  /** Launcher shadow strength: none | soft | medium | strong. */
  launcherShadow?: string | undefined;
  /** Launcher idle motion: none | pulse | bounce | float. */
  launcherMotion?: string | undefined;
}

const SHADOW_VALUES = new Set(['none', 'soft', 'medium', 'strong']);
const MOTION_VALUES = new Set(['none', 'pulse', 'bounce', 'float']);

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  messageId?: string;
  feedback?: 'positive' | 'negative' | null;
}

// Refresh a bit before actual expiry so a send never races an expiring token.
const SESSION_REFRESH_SLACK_MS = 5000;
// Where the "Powered by ASK360" footer link goes.
const PRODUCT_URL = 'https://chat.udarawijesinghe.com';
// About five lines of text before the message box starts scrolling.
const MAX_INPUT_HEIGHT_PX = 120;
// Within this many px of the end counts as "at the bottom" for auto-follow.
const NEAR_BOTTOM_PX = 40;
// The async Clipboard API exists only in secure contexts (https, localhost);
// on a plain-http host page the copy button is simply not offered.
const CAN_COPY = typeof navigator !== 'undefined' && !!navigator.clipboard;

export function Widget(props: WidgetProps) {
  const { apiUrl, siteKey, title, position, iconUrl } = props;
  const suggestedQuestions = (props.suggestedQuestions ?? []).slice(0, 4);

  const shadow =
    props.launcherShadow && SHADOW_VALUES.has(props.launcherShadow) ? props.launcherShadow : 'soft';
  const motion =
    props.launcherMotion && MOTION_VALUES.has(props.launcherMotion) ? props.launcherMotion : 'none';
  const inputPlaceholder = props.inputPlaceholder?.trim() || 'Type a message...';

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  // Seconds left before another message can be sent, from a 429's
  // `Retry-After` header.
  const [retryIn, setRetryIn] = useState(0);
  // Index of the reply whose copy button is showing its ✓ confirmation.
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const sessionRef = useRef<WidgetSession | null>(null);
  const chatIdRef = useRef<string | undefined>(undefined);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);
  // Whether the message list is scrolled to (near) the bottom — a ref, not
  // state, since it changes on every scroll event and drives no render itself.
  const atBottomRef = useRef(true);
  // True while our own smooth scroll (the "New message" pill) is animating.
  const smoothScrollingRef = useRef(false);
  const [showNewPill, setShowNewPill] = useState(false);
  // Bumped by "New conversation": a reply still in flight from the previous
  // conversation checks it and drops its callbacks instead of writing into
  // the new one. `abortRef` cancels that reply's network stream outright.
  const conversationRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  // On open, move focus into the panel; on close, hand it back to the
  // launcher so a keyboard user isn't dropped at the top of the host page.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else if (wasOpenRef.current) {
      requestAnimationFrame(() => bubbleRef.current?.focus());
    }
    wasOpenRef.current = open;
  }, [open]);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = messagesRef.current;
    if (!el) return;
    smoothScrollingRef.current = smooth;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    atBottomRef.current = true;
    setShowNewPill(false);
  }, []);

  const handleScroll = useCallback(() => {
    const el = messagesRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    // Mid-animation positions of our own smooth scroll aren't the visitor
    // scrolling up — ignore them until it lands, or a delta arriving during
    // the animation would re-raise the pill the visitor just clicked.
    if (smoothScrollingRef.current) {
      if (atBottom) smoothScrollingRef.current = false;
      return;
    }
    atBottomRef.current = atBottom;
    if (atBottom) setShowNewPill(false);
  }, []);

  // A real wheel/touch gesture hands control back to the visitor at once.
  const cancelSmoothScroll = useCallback(() => {
    smoothScrollingRef.current = false;
  }, []);

  // Changes only when content is added (a message, or streamed text) — not on
  // a feedback click, so rating an old reply never raises the pill.
  const lastMessage = messages[messages.length - 1];
  const waiting = sending && lastMessage?.role === 'assistant' && !lastMessage.content;
  const contentKey = `${messages.length}:${lastMessage?.content.length ?? 0}:${waiting}`;

  // Follow new content while the visitor is at the bottom. If they've
  // scrolled up to read, leave them there and offer the "New message" pill.
  useEffect(() => {
    if (!messagesRef.current) return;
    if (atBottomRef.current) scrollToBottom();
    else setShowNewPill(true);
  }, [contentKey, scrollToBottom]);

  // The message list remounts at the top each time the panel opens.
  useEffect(() => {
    if (open) scrollToBottom();
  }, [open, scrollToBottom]);

  useEffect(() => {
    if (retryIn <= 0) return;
    const id = setInterval(() => setRetryIn((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(id);
  }, [retryIn]);

  const ensureSession = useCallback(async (): Promise<WidgetSession | null> => {
    const current = sessionRef.current;
    if (current && current.expiresAt - SESSION_REFRESH_SLACK_MS > Date.now()) {
      return current;
    }
    try {
      const session = await fetchWidgetSession(apiUrl, siteKey);
      sessionRef.current = session;
      setSessionError(null);
      return session;
    } catch (err) {
      setSessionError(
        err instanceof WidgetSessionError ? err.message : 'Chat is unavailable right now.',
      );
      return null;
    }
  }, [apiUrl, siteKey]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    void ensureSession();
  }, [ensureSession]);

  const updateLastMessage = (updater: (last: ChatMessage) => ChatMessage) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice();
      const last = next[next.length - 1];
      if (!last) return prev;
      next[next.length - 1] = updater(last);
      return next;
    });
  };

  const handleSend = useCallback(
    async (text: string) => {
      if (!text.trim() || sending || retryIn > 0) return;
      setSending(true);
      // The visitor's own message always brings them back to the bottom.
      atBottomRef.current = true;
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text },
        { role: 'assistant', content: '' },
      ]);

      const conversation = conversationRef.current;
      const current = () => conversation === conversationRef.current;

      const session = await ensureSession();
      if (!current()) return;
      if (!session) {
        updateLastMessage(() => ({
          role: 'assistant',
          content: 'Sorry, chat is unavailable right now.',
        }));
        setSending(false);
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      await streamChat(
        apiUrl,
        session.token,
        session.botProfileId,
        text,
        chatIdRef.current,
        {
          onDelta: (delta) => {
            if (current())
              updateLastMessage((last) => ({ ...last, content: last.content + delta }));
          },
          onDone: (data) => {
            if (!current()) return;
            chatIdRef.current = data.chat_id;
            updateLastMessage((last) => ({ ...last, messageId: data.message_id, feedback: null }));
          },
          onError: (message, retryAfterSeconds) => {
            if (!current()) return;
            updateLastMessage((last) =>
              last.content ? last : { ...last, content: `Sorry, something went wrong: ${message}` },
            );
            if (retryAfterSeconds) setRetryIn(retryAfterSeconds);
          },
        },
        controller.signal,
      );

      if (current()) {
        // A stream can end with no text and no error (an empty completion, or
        // a proxy closing the connection early). The placeholder is hidden
        // while empty, so without this the reply would silently vanish.
        updateLastMessage((last) =>
          last.role === 'assistant' && !last.content
            ? { ...last, content: "Sorry, I couldn't generate a reply. Please try again." }
            : last,
        );
        setSending(false);
      }
    },
    [apiUrl, ensureSession, sending, retryIn],
  );

  // Grow the message box with its content, up to MAX_INPUT_HEIGHT_PX, then
  // let it scroll.
  const autosizeInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    // `scrollHeight` excludes the border but the box is `border-box`, so add
    // it back — otherwise each line comes up 2px short and a scrollbar shows.
    const full = el.scrollHeight + el.offsetHeight - el.clientHeight;
    el.style.height = `${Math.min(full, MAX_INPUT_HEIGHT_PX)}px`;
    // No scrollbar until the box has actually hit its cap.
    el.style.overflowY = full > MAX_INPUT_HEIGHT_PX ? 'auto' : 'hidden';
  }, []);

  // The box stays editable while a reply streams, so the visitor can type the
  // next question; only sending waits. Nothing is cleared until it's sent.
  const submitInput = useCallback(() => {
    const el = inputRef.current;
    const value = el?.value.trim();
    if (!el || !value || sending || retryIn > 0) return;
    el.value = '';
    autosizeInput();
    void handleSend(value);
  }, [autosizeInput, handleSend, sending, retryIn]);

  const handleSubmit = useCallback(
    (e: Event) => {
      e.preventDefault();
      submitInput();
    },
    [submitInput],
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Enter sends, Shift+Enter is a newline. An IME (e.g. Japanese/Chinese
      // input) uses Enter to confirm a character, not send: `isComposing`
      // covers most browsers, keyCode 229 covers Safari, which reports the
      // confirming Enter with isComposing === false.
      if (e.key === 'Enter' && !e.shiftKey && !e.isComposing && e.keyCode !== 229) {
        e.preventDefault();
        submitInput();
      }
    },
    [submitInput],
  );

  const handleFeedback = useCallback(
    (index: number, value: 'positive' | 'negative') => {
      const msg = messages[index];
      const session = sessionRef.current;
      const chatId = chatIdRef.current;
      if (!msg?.messageId || !session || !chatId) return;

      const next = msg.feedback === value ? null : value;
      setMessages((prev) => {
        const copy = prev.slice();
        copy[index] = { ...msg, feedback: next };
        return copy;
      });
      void submitFeedback(apiUrl, session.token, chatId, msg.messageId, next);
    },
    [apiUrl, messages],
  );

  // Clears the panel; the next message starts a fresh chat server-side (no
  // chat_id). The old chat is untouched and stays in the dashboard's history.
  const handleNewConversation = useCallback(() => {
    conversationRef.current++;
    abortRef.current?.abort();
    abortRef.current = null;
    chatIdRef.current = undefined;
    setMessages([]);
    setSending(false);
    setCopiedIndex(null);
    atBottomRef.current = true;
    setShowNewPill(false);
    inputRef.current?.focus();
  }, []);

  // Copies the reply as the visitor sees it (rendered text, no `**`/`#`
  // markers), falling back to the raw text if the bubble isn't found.
  const handleCopy = useCallback(
    async (index: number, button: EventTarget | null) => {
      const md = (button as HTMLElement | null)
        ?.closest('.mrw-row')
        ?.querySelector<HTMLElement>('.mrw-md');
      const text = (md?.innerText ?? messages[index]?.content ?? '').trim();
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        return; // Permission denied or no focus — leave the icon as it was.
      }
      setCopiedIndex(index);
      window.setTimeout(() => setCopiedIndex((c) => (c === index ? null : c)), 1500);
    },
    [messages],
  );

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    // Focus trap: keep Tab / Shift+Tab inside the open panel. The widget lives
    // in a shadow root, so the focused node is `shadowRoot.activeElement`, not
    // `document.activeElement` (which would be the host element).
    if (e.key !== 'Tab' || !windowRef.current) return;
    const focusables = Array.from(
      windowRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      ),
    );
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const root = windowRef.current.getRootNode();
    const active = root instanceof ShadowRoot ? root.activeElement : document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // The reply placeholder is added the moment a message is sent, empty until
  // the first streamed token. Until then only the typing dots show (`waiting`)
  // — never an empty bubble beside them — and the dots go away once text
  // arrives.
  const rows = messages.map((m, i) => ({ m, i })).filter(({ m }) => m.role === 'user' || m.content);
  const lastRowRole = rows[rows.length - 1]?.m.role;

  return (
    <div
      class="mrw-root"
      data-position={position}
      data-shadow={shadow}
      data-motion={motion}
      onKeyDown={handleKeyDown}
    >
      {!open && (
        <button
          ref={bubbleRef}
          type="button"
          class={`mrw-bubble${iconUrl ? ' mrw-bubble--icon' : ''}`}
          onClick={handleOpen}
          aria-label={`Open ${title}`}
        >
          {iconUrl ? <img src={iconUrl} alt="" class="mrw-bubble-icon" /> : <BubbleIcon />}
        </button>
      )}

      {open && (
        <div ref={windowRef} class="mrw-window" role="dialog" aria-modal="true" aria-label={title}>
          <div class="mrw-header">
            <span class="mrw-header-id">
              {iconUrl && <img src={iconUrl} alt="" class="mrw-header-icon" />}
              <span class="mrw-header-title">{title}</span>
            </span>
            <span class="mrw-header-actions">
              {messages.length > 0 && (
                <button
                  type="button"
                  class="mrw-header-btn"
                  onClick={handleNewConversation}
                  aria-label="Start a new conversation"
                  title="New conversation"
                >
                  <NewChatIcon />
                </button>
              )}
              <button
                type="button"
                class="mrw-header-close"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
              >
                &times;
              </button>
            </span>
          </div>

          {sessionError && (
            <div class="mrw-banner" role="alert">
              {sessionError}
            </div>
          )}

          <div class="mrw-body">
            <div
              class="mrw-messages"
              ref={messagesRef}
              aria-live="polite"
              onScroll={handleScroll}
              onWheel={cancelSmoothScroll}
              onTouchStart={cancelSmoothScroll}
            >
              {/* Starter questions — shown only before the conversation begins.
                Tapping one sends it as the first message; the visitor can
                also just type instead. */}
              {messages.length === 0 && suggestedQuestions.length > 0 && (
                <div class="mrw-suggestions">
                  {suggestedQuestions.map((q) => (
                    <button
                      type="button"
                      class="mrw-suggestion"
                      key={q}
                      disabled={sending}
                      onClick={() => void handleSend(q)}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Consecutive messages from one sender form a group: tighter
                spacing, and only the first carries the avatar (bot side) and
                the "tail" corner. The typing dots continue the bot's group. */}
              {rows.map(({ m, i }, k) => {
                const first = rows[k - 1]?.m.role !== m.role;
                return (
                  <div
                    key={i}
                    class={`mrw-row mrw-row-${m.role}${first ? ' mrw-row--first' : ' mrw-row--cont'}`}
                  >
                    {m.role === 'assistant' && <Avatar show={first} iconUrl={iconUrl} />}
                    {m.role === 'assistant' ? (
                      <div class="mrw-stack">
                        <div class="mrw-msg mrw-msg-assistant">
                          <MarkdownView text={m.content} />
                        </div>

                        {/* Shown once the reply is complete; revealed on hover /
                          keyboard focus (always on touch screens) — widget.css. */}
                        {m.messageId && (
                          <div class="mrw-actions">
                            {CAN_COPY && (
                              <button
                                type="button"
                                aria-label={copiedIndex === i ? 'Copied' : 'Copy reply'}
                                title={copiedIndex === i ? 'Copied' : 'Copy'}
                                onClick={(e) => void handleCopy(i, e.currentTarget)}
                              >
                                {copiedIndex === i ? <CheckIcon /> : <CopyIcon />}
                              </button>
                            )}
                            <button
                              type="button"
                              aria-pressed={m.feedback === 'positive'}
                              aria-label="Good response"
                              title="Good response"
                              onClick={() => handleFeedback(i, 'positive')}
                            >
                              <ThumbUpIcon />
                            </button>
                            <button
                              type="button"
                              aria-pressed={m.feedback === 'negative'}
                              aria-label="Bad response"
                              title="Bad response"
                              onClick={() => handleFeedback(i, 'negative')}
                            >
                              <ThumbDownIcon />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div class="mrw-msg mrw-msg-user">{m.content}</div>
                    )}
                  </div>
                );
              })}

              {waiting && (
                <div
                  class={`mrw-row mrw-row-assistant${
                    lastRowRole === 'assistant' ? ' mrw-row--cont' : ' mrw-row--first'
                  }`}
                  aria-hidden="true"
                >
                  <Avatar show={lastRowRole !== 'assistant'} iconUrl={iconUrl} />
                  <div class="mrw-typing">
                    <span class="mrw-typing-dot" />
                    <span class="mrw-typing-dot" />
                    <span class="mrw-typing-dot" />
                  </div>
                </div>
              )}
            </div>

            {showNewPill && (
              <button type="button" class="mrw-new-pill" onClick={() => scrollToBottom(true)}>
                <ArrowDownIcon />
                New message
              </button>
            )}
          </div>

          <form class="mrw-input-bar" onSubmit={handleSubmit}>
            <label class="mrw-sr-only" htmlFor="mrw-input">
              Message
            </label>
            <textarea
              id="mrw-input"
              ref={inputRef}
              class="mrw-input"
              rows={1}
              placeholder={retryIn > 0 ? `Wait ${retryIn}s...` : inputPlaceholder}
              disabled={retryIn > 0}
              autocomplete="off"
              onInput={autosizeInput}
              onKeyDown={handleInputKeyDown}
            />
            <button
              type="submit"
              class="mrw-send"
              disabled={sending || retryIn > 0}
              aria-label="Send message"
            >
              <SendIcon />
            </button>
          </form>

          <div class="mrw-powered">
            Powered by{' '}
            <a href={PRODUCT_URL} target="_blank" rel="noopener noreferrer">
              <img src={brandIconUrl} alt="" class="mrw-powered-icon" />
              ASK360
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/** The bot's avatar beside the first message of each of its groups — the
 * tenant's launcher icon when set, else the chat glyph on the header colour.
 * Continuation messages get an invisible spacer so the bubbles stay aligned. */
function Avatar({ show, iconUrl }: { show: boolean; iconUrl: string | undefined }) {
  if (!show) return <span class="mrw-avatar mrw-avatar--spacer" aria-hidden="true" />;
  return (
    <span class={`mrw-avatar${iconUrl ? ' mrw-avatar--img' : ''}`} aria-hidden="true">
      {iconUrl ? <img src={iconUrl} alt="" /> : <BubbleIcon />}
    </span>
  );
}

function BubbleIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
      />
    </svg>
  );
}

function NewChatIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M12 8v6m-3-3h6M9 18H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-5l-5 4v-4z"
      />
    </svg>
  );
}

function ArrowDownIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2.5"
        d="M12 5v14m-6-6l6 6 6-6"
      />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="9" y="9" width="11" height="11" rx="2" stroke-width="2" />
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M5 15H4a1 1 0 01-1-1V4a1 1 0 011-1h10a1 1 0 011 1v1"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ThumbUpIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.264 21H9a2 2 0 01-2-2v-8a2 2 0 01.5-1.32L13 4l1 1v5z"
      />
    </svg>
  );
}

function ThumbDownIcon() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3H15a2 2 0 012 2v8a2 2 0 01-.5 1.32L10 20l-1-1v-5z"
      />
    </svg>
  );
}
